import * as Sentry from '@sentry/browser';
import { omit } from 'lodash';

import { CustomError } from 'src/errorHandling/CustomError';

export class Http {
  public async makeUnAuthenticatedRequest<P, R = P>(
    method: string,
    path: string,
    data: P | FormData | null = null,
    useFormData?: boolean,
  ): Promise<R> {
    const options = this.prepareOptions(method, data, useFormData);

    try {
      const res = await fetch(path, options);

      if (res.ok) {
        return res.json();
      }

      const contentType = res.headers.get('content-type');
      const errorBody =
        contentType && contentType.includes('application/json')
          ? await res.json()
          : await res.text();

      const errorMessage = errorBody
        ? JSON.stringify(errorBody)
        : 'Unauthenticated request failed.';

      throw new CustomError(errorMessage, res.statusText, res.status, errorBody);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      Sentry.captureException(error);

      return Promise.reject(error);
    }
  }

  private prepareOptions(method: string, data: any, useFormData?: boolean): RequestInit {
    let body: FormData | string | null;
    if (useFormData || (method === 'GET' && data)) {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      body = formData;
    } else if (data) {
      body = JSON.stringify(data);
    } else {
      body = null;
    }

    const headers = new Headers() as any;

    if (!useFormData) {
      headers['Content-Type'] = 'application/json';
    }

    return {
      body,
      headers,
      method,
    };
  }

  public formDataPATCH<P, R = P>(path: string, body: P): Promise<R> {
    return this.makeRequest<P, R>('PATCH', path, this.prepareFormData<P>(body), true);
  }

  public formDataPOST<P, R = P>(path: string, body: P): Promise<R> {
    return this.makeRequest<P, R>('POST', path, this.prepareFormData<P>(body), true);
  }

  public PATCH<P, R = P>(path: string, body: P): Promise<R> {
    return this.makeRequest<P, R>('PATCH', path, body);
  }

  public POST<P, R = P>(path: string, body: P): Promise<R> {
    return this.makeRequest<P, R>('POST', path, body);
  }

  public DELETE<R>(path: string): Promise<R> {
    return this.makeRequest<R>('DELETE', path);
  }

  private prepareFormData<T>(data: T): FormData {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => formData.append(key, value || ''));

    return formData;
  }

  private async makeRequest<P, R = P>(
    method: string,
    path: string,
    data: P | FormData | null = null,
    useFormData?: boolean,
  ): Promise<R> {
    const body = useFormData || method === 'GET' ? data : JSON.stringify(data);

    const jwt = localStorage.getItem('auth');

    const headers = omit(
      {
        Authorization: jwt && `Bearer ${JSON.parse(jwt).access_token}`,
        'Content-Type': 'application/json',
      },
      useFormData ? 'Content-Type' : '',
    );

    const options = {
      body,
      headers,
      method,
    } as RequestInit;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_DEFAULT_WEBAPP_API_ENDPOINT}/${path}`,
        options,
      );

      if (res.ok) {
        return method === 'DELETE' ? null : res.json();
      }

      const contentType = res.headers.get('content-type');
      const errorBody =
        contentType && contentType.includes('application/json')
          ? await res.json()
          : await res.text();

      const errorMessage = errorBody ? JSON.stringify(errorBody) : 'Authenticated request failed.';

      throw new CustomError(errorMessage, res.statusText, res.status, errorBody);
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error(error);
      Sentry.captureMessage(error);

      return Promise.reject(error);
    }
  }
}

const http = new Http();
export default http;
